import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, Card, CardContent, Grid,
  TextField, InputAdornment, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Paper,
  CssBaseline, Tabs, Tab, Badge,
} from '@mui/material';
import {
  Add, Search as SearchIcon, LocalShippingOutlined, CheckCircleOutline,
  HourglassEmptyOutlined, CancelOutlined, WarehouseOutlined, CallReceivedOutlined,
  BlockOutlined, ReceiptLongOutlined,
} from '@mui/icons-material';
import AppTheme from '../../../shared-theme/AppTheme';
import SideMenu from '../../../components/SideMenu';
import Header from '../../../components/Header';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import { useNavigate } from 'react-router-dom';

const ESTADO_META = {
  pendiente:    { label: 'Pendiente',    color: 'warning', Icon: HourglassEmptyOutlined },
  en_transito:  { label: 'En tránsito',  color: 'info',    Icon: LocalShippingOutlined },
  recibida:     { label: 'Recibida',     color: 'success', Icon: CheckCircleOutline },
  rechazada:    { label: 'Rechazada',    color: 'error',   Icon: BlockOutlined },
  cancelada:    { label: 'Cancelada',    color: 'default', Icon: CancelOutlined },
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const formatFecha = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const MetricCard = ({ label, value, accent, Icon }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', height: '100%', position: 'relative', overflow: 'hidden',
      '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: 3, height: '100%', bgcolor: accent },
    }}
  >
    <CardContent sx={{ py: 2, px: 2.5, pl: 3, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>{label}</Typography>
          <Typography fontWeight={700} sx={{ fontSize: '1.25rem', lineHeight: 1.2, color: accent }}>{value}</Typography>
        </Box>
        {Icon && <Icon sx={{ color: accent, fontSize: 28, opacity: 0.8 }} />}
      </Stack>
    </CardContent>
  </Card>
);

export default function TransferenciasMercancia() {
  const navigate = useNavigate();
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('todas');
  const usuarioId = localStorage.getItem('id');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/${localStorage.getItem('idcompany')}`, { headers: authHeaders() });
      const data = await res.json();
      setTransferencias(Array.isArray(data) ? data : []);
    } catch {
      setTransferencias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Particiones por rol: lo que envío vs lo que recibo (sólo en_transito es accionable).
  const particiones = useMemo(() => ({
    porEnviar: transferencias.filter(t => t.estado === 'pendiente' && t.created_by === usuarioId),
    porRecibir: transferencias.filter(t => t.estado === 'en_transito'),
    historico: transferencias.filter(t => ['recibida','rechazada','cancelada'].includes(t.estado)),
  }), [transferencias, usuarioId]);

  const porTab = useMemo(() => {
    switch (tab) {
      case 'por_enviar':  return particiones.porEnviar;
      case 'por_recibir': return particiones.porRecibir;
      case 'historico':   return particiones.historico;
      default:            return transferencias;
    }
  }, [tab, particiones, transferencias]);

  const filtered = useMemo(() => {
    if (!search) return porTab;
    const term = search.toLowerCase();
    return porTab.filter(t =>
      (t.codigo || '').toLowerCase().includes(term)
      || (t.almacen_origen_nombre || '').toLowerCase().includes(term)
      || (t.almacen_destino_nombre || '').toLowerCase().includes(term)
      || (t.transportista || '').toLowerCase().includes(term),
    );
  }, [porTab, search]);

  const metrics = useMemo(() => ({
    pendientes: transferencias.filter(t => t.estado === 'pendiente').length,
    transito: transferencias.filter(t => t.estado === 'en_transito').length,
    recibidas: transferencias.filter(t => t.estado === 'recibida').length,
    rechazadas: transferencias.filter(t => t.estado === 'rechazada').length,
  }), [transferencias]);

  return (
    <Box>
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack sx={{ alignItems: 'stretch', pb: 6, mt: { xs: 8, md: 0 } }}>
              <Header parent="Logística" child="Transferencias de Mercancía" />

              <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                  <Box>
                    <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">Transferencias de Mercancía</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.25}>Envíos entre almacenes con seguimiento completo</Typography>
                  </Box>
                  <Button
                    variant="contained" color="primarybutton" startIcon={<Add />}
                    onClick={() => navigate('/logistica/transferencias/crear')}
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                  >
                    Nueva transferencia
                  </Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}><MetricCard label="Pendientes" value={metrics.pendientes} accent="#ed6c02" Icon={HourglassEmptyOutlined} /></Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}><MetricCard label="En tránsito" value={metrics.transito} accent="#0288d1" Icon={LocalShippingOutlined} /></Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}><MetricCard label="Recibidas" value={metrics.recibidas} accent="#2e7d32" Icon={CheckCircleOutline} /></Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}><MetricCard label="Rechazadas" value={metrics.rechazadas} accent="#c62828" Icon={BlockOutlined} /></Grid>
                </Grid>

                <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, boxShadow: 'none' }}>
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1.5 }}>
                    <Tabs
                      value={tab} onChange={(_, v) => setTab(v)}
                      variant="scrollable" scrollButtons="auto"
                      slotProps={{ indicator: { sx: { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' } } }}
                      sx={{ minHeight: 48, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 48, px: 2, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main', fontWeight: 700 } } }}
                    >
                      <Tab icon={<ReceiptLongOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={`Todas · ${transferencias.length}`} value="todas" />
                      <Tab icon={<HourglassEmptyOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={`Por enviar · ${particiones.porEnviar.length}`} value="por_enviar" />
                      <Tab
                        icon={<CallReceivedOutlined sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                          <Badge color="error" badgeContent={particiones.porRecibir.length} max={99} sx={{ '& .MuiBadge-badge': { right: -18, top: 2 } }}>
                            Por recibir
                          </Badge>
                        }
                        value="por_recibir"
                      />
                      <Tab icon={<CheckCircleOutline sx={{ fontSize: 18 }} />} iconPosition="start" label={`Histórico · ${particiones.historico.length}`} value="historico" />
                    </Tabs>
                  </Box>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <TextField
                      fullWidth size="small"
                      placeholder="Buscar por código, almacén o transportista"
                      value={search} onChange={(e) => setSearch(e.target.value)}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                    />
                  </CardContent>
                </Card>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledCellHeader>Código</StyledCellHeader>
                        <StyledCellHeader>Origen → Destino</StyledCellHeader>
                        <StyledCellHeader>Transportista</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'center' }}>Items</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'center' }}>Enviado / Recibido</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'center' }}>Estado</StyledCellHeader>
                        <StyledCellHeader>Creada</StyledCellHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && (
                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>Cargando transferencias…</TableCell></TableRow>
                      )}
                      {!loading && filtered.length === 0 && (
                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No hay transferencias en esta vista.</TableCell></TableRow>
                      )}
                      {filtered.map((t) => {
                        const meta = ESTADO_META[t.estado] || ESTADO_META.pendiente;
                        const hayRechazos = (t.items_rechazados || 0) > 0;
                        return (
                          <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/logistica/transferencias/detalle/${t.id}`)}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>{t.codigo}</Typography>
                              {t.transferencia_original_id && (
                                <Typography variant="caption" color="warning.main" fontWeight={600}>Devolución</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2" fontWeight={600}>{t.almacen_origen_nombre || '—'}</Typography>
                                <Typography variant="caption" color="text.secondary">→</Typography>
                                <Typography variant="body2" fontWeight={600}>{t.almacen_destino_nombre || '—'}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{t.transportista || '—'}</Typography></TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{t.items_count || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2">
                                {t.total_enviado || 0} / <Typography component="span" variant="body2" color={hayRechazos ? 'error.main' : 'text.primary'} fontWeight={600}>{t.total_recibido || 0}</Typography>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Chip size="small" icon={<meta.Icon sx={{ fontSize: 14 }} />} label={meta.label} color={meta.color} sx={{ fontWeight: 600 }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatFecha(t.created_at)}</Typography>
                              <Typography variant="caption" color="text.secondary">por {t.created_by_nombre || '—'}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </Box>
        </Box>
      </AppTheme>
    </Box>
  );
}
